<template>
    <v-input
        ref="elInput"
        :error-messages="vInputErrors"
        hide-details
    >
        <div class="flex-grow-1">
            <div>
                <h4
                    class="py-0 mt-0 mb-3"
                >
                    Who is it for?
                </h4>
                <v-row
                    density="compact"
                >
                    <v-col
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            v-model.lazy="recipientName"
                            v-bind="recipientNameProps"
                            variant="outlined"
                            name="recipientName"
                            label="Recipient Name"
                            hide-details="auto"
                            clearable
                        ></v-text-field>
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            v-model.lazy="recipientEmail"
                            v-bind="recipientEmailProps"
                            type="email"
                            variant="outlined"
                            name="recipientEmail"
                            label="Recipient Email"
                            hide-details="auto"
                            clearable
                        ></v-text-field>
                    </v-col>
                </v-row>
            </div>

            <div
                class="mt-4"
            >
                <h4
                    class="py-0 mt-0 mb-3"
                >
                    Who is it from?
                </h4>

                <v-row
                    density="compact"
                >
                    <v-col
                        cols="12"
                    >
                        <v-text-field
                            v-model.lazy="senderName"
                            v-bind="senderNameProps"
                            variant="outlined"
                            name="senderName"
                            label="Sender Name"
                            hide-details="auto"
                            clearable
                        ></v-text-field>
                    </v-col>
                </v-row>
            </div>
            <div
                class="mt-4"
            >
                <h4
                    class="py-0 mt-0 mb-3"
                >
                    Add a personalization message (optional)
                </h4>

                <v-row
                    density="compact"
                >
                    <v-col
                        cols="12"
                    >
                        <v-textarea
                            v-model.lazy="messageToRecipient"
                            v-bind="messageToRecipientProps"
                            variant="outlined"
                            name="messageToRecipient"
                            label="Message to Recipient"
                            hide-details="auto"
                            rows="1"
                            counter
                            auto-grow
                            clearable
                        ></v-textarea>
                    </v-col>
                </v-row>
            </div>
        </div>
    </v-input>
</template>

<script lang="ts" setup>
    import type { SubmissionContext } from 'vee-validate';
    import type { PropType } from 'vue';
    import type { CartLineItemPersonalization } from '~/types';
    import { useForm } from 'vee-validate';
    import { toTypedSchema } from '@vee-validate/valibot';
    import { ProductPersonalizationRequestModel } from '~/models';
    import { useFormValidation } from 'x1-base/composables';
    import type { VInput } from 'vuetify/components';

    const props = defineProps({
        modelValue: {
            type: Object as PropType<Nilish<CartLineItemPersonalization>>,
            default: null
        }
    });

    const emit = defineEmits<{
        (e: 'update:modelValue', value: Nilish<CartLineItemPersonalization>): void;
        (e: 'on-exit'): void;
    }>();

    const elInput = shallowRef<InstanceType<typeof VInput>>();
    const { getDefineFieldOptions } = useFormValidation();

    const {
        handleSubmit,
        values: formValues,
        defineField,
        meta: formMeta,
        resetForm,
        setErrors
    } = useForm({
        validationSchema: toTypedSchema(
            ProductPersonalizationRequestModel
        )
    });

    const optsDefineField = getDefineFieldOptions('vuetify', {
        validateOnBlur: true,
        validateOnChange: true,
        validateOnInput: false,
        validateOnModelUpdate: true
    });

    const [senderName, senderNameProps] = defineField('senderName', optsDefineField);
    const [recipientName, recipientNameProps] = defineField('recipientName', optsDefineField);
    const [recipientEmail, recipientEmailProps] = defineField('recipientEmail', optsDefineField);
    const [messageToRecipient, messageToRecipientProps] = defineField('messageToRecipient', optsDefineField);
    const vInputErrors = computed<Nullable<string[]>>(() => {
        const { valid, dirty, touched } = formMeta.value;

        if (valid && !dirty && !touched) {
            return ['dirty'];
        }

        return valid ? null : ['errors'];
    });

    const unWatchFormValues = watch(
        formValues,
        (values) => {
            emit('update:modelValue', values as CartLineItemPersonalization);
        }
    );

    const onSubmit = handleSubmit(async (values: typeof formValues, ctx: SubmissionContext) => {
        const e = ctx.evt as Event;
        e.preventDefault();
        emit('update:modelValue', (values as CartLineItemPersonalization) ?? null);
    });

    onBeforeUnmount(() => {
        unWatchFormValues();
    });

    defineExpose({ resetForm });
</script>

<style lang="scss"></style>
